import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import {
  AccountData,
  AdminSwapPassRequest,
  PassOrderPageData,
  PassOut,
  PassScheduleSelectionDTO,
  PassService,
  PassSwapErrorCode,
  PassType,
} from '../../../../api';
import { useAuth } from '../../../../session/InternalAuthProvider';
import { useLanguage } from '../../../../common/GeneralUtils';
import { useApiResponseHandlerContext } from '../../../../common/apiNotifications/useApiResponseHandlerContext';
import { ApiResponseHandlerType } from '../../../../common/apiNotifications/ApiResponseHandlerContext';
import { getPassOrderPages } from '../../../../utils/AccountUtils';
import { getDateFormat } from '../../../../common/getDateAndTimeFormat';
import { getCurrencySymbol } from '../../../../utils/utils';
import { isRecurringPassType } from '../../utils';
import { FixedSchedulePicker } from '../schedulePanel/FixedSchedulePicker';
import { resolveSwapError } from './swapErrors';

type Props = {
  open: boolean;
  onClose: () => void;
  pass: PassOut;
  currentPassOrderPage: PassOrderPageData;
  onUpdated: (pass: PassOut) => void;
};

const REFRESH_AVAILABILITY_CODES = new Set<PassSwapErrorCode>([
  PassSwapErrorCode.SLOT_AT_CAPACITY,
  PassSwapErrorCode.SLOT_NOT_IN_SCHEDULE,
  PassSwapErrorCode.SLOT_NOT_REDEEMABLE,
]);

const RESET_TARGET_CODES = new Set<PassSwapErrorCode>([
  PassSwapErrorCode.TARGET_PASS_ORDER_PAGE_NOT_FOUND,
  PassSwapErrorCode.TARGET_PASS_ORDER_PAGE_ARCHIVED,
  PassSwapErrorCode.ALREADY_ON_TARGET_PASS_ORDER_PAGE,
]);

export const SwapPassDialog = ({ open, onClose, pass, currentPassOrderPage, onUpdated }: Props) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { showResponseNotification } = useApiResponseHandlerContext();
  const dateFormat = getDateFormat(account).longDateFormat;
  const currencySymbol = getCurrencySymbol(account);

  const targets = useMemo(
    () =>
      getPassOrderPages(account).filter(
        (page) => isRecurringPassType(page.pass_type) && page.id !== pass.order_page_id,
      ),
    [account, pass.order_page_id],
  );

  // Without a cycle end date the server refuses a scheduled swap, so "now" is the only option.
  const canSchedule = !!pass.expiration_date;

  const [targetId, setTargetId] = useState('');
  const [applyImmediately, setApplyImmediately] = useState(!canSchedule);
  const [selections, setSelections] = useState<PassScheduleSelectionDTO[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [providerError, setProviderError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!open) return;
    setTargetId('');
    setApplyImmediately(!canSchedule);
    setSelections([]);
    setInlineError(null);
    setProviderError(null);
  }, [open, canSchedule]);

  const target = targets.find((page) => page.id === targetId);
  const targetIsFixed = target?.pass_type === PassType.FIXED_SCHEDULE_MONTHLY;
  const requiredSlots = targetIsFixed ? target?.weekly_session_count ?? 0 : 0;
  const slotsSatisfied = !targetIsFixed || selections.length === requiredSlots;
  const leavingFixedSchedule = applyImmediately && currentPassOrderPage.pass_type === PassType.FIXED_SCHEDULE_MONTHLY;
  const nextChargeLabel = pass.next_charge_date ? moment(pass.next_charge_date).format(dateFormat) : '';

  const handleSubmit = async () => {
    if (!target) return;
    setSubmitting(true);
    setInlineError(null);
    setProviderError(null);
    try {
      const request: AdminSwapPassRequest = {
        target_pass_order_page_id: target.id,
        apply_immediately: applyImmediately,
        ...(targetIsFixed ? { schedule_selections: selections } : {}),
      };
      const updated = await PassService.swapPass(pass.id, request);
      onUpdated(updated);
      showResponseNotification(
        t(applyImmediately ? 'passes.swap.swapped_toast' : 'passes.swap.swap_scheduled_toast'),
        ApiResponseHandlerType.SUCCESS,
      );
      onClose();
    } catch (e) {
      const { code, message, providerError: raw } = resolveSwapError(e, t, account, language, dateFormat);
      if (code && REFRESH_AVAILABILITY_CODES.has(code)) {
        showResponseNotification(message, ApiResponseHandlerType.ERROR);
        setSelections([]);
        setRefreshKey((k) => k + 1);
      } else {
        if (code && RESET_TARGET_CODES.has(code)) setTargetId('');
        setInlineError(message);
        if (raw) setProviderError(raw);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('passes.swap.dialog_title')}</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2}>
          <FormControl fullWidth size="small">
            <InputLabel id="swap-target-label">{t('passes.swap.target_label')}</InputLabel>
            <Select
              labelId="swap-target-label"
              label={t('passes.swap.target_label')}
              value={targetId}
              onChange={(event) => {
                setTargetId(event.target.value);
                setSelections([]);
                setInlineError(null);
              }}>
              {targets.map((page) => (
                <MenuItem key={page.id} value={page.id}>
                  {page.label?.[language] || page.label?.en || page.id} · {page.price}
                  {currencySymbol}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {target && (
            <Typography variant="body2">
              {t('passes.swap.price_delta', {
                from: `${currentPassOrderPage.price}${currencySymbol}`,
                to: `${target.price}${currencySymbol}`,
                date: nextChargeLabel,
              })}
            </Typography>
          )}

          <FormControl>
            <RadioGroup
              value={applyImmediately ? 'now' : 'next_cycle'}
              onChange={(event) => setApplyImmediately(event.target.value === 'now')}>
              <FormControlLabel
                value="next_cycle"
                control={<Radio size="small" />}
                disabled={!canSchedule}
                label={t('passes.swap.mode_next_cycle', { date: nextChargeLabel })}
              />
              <FormControlLabel value="now" control={<Radio size="small" />} label={t('passes.swap.mode_now')} />
            </RadioGroup>
          </FormControl>

          {leavingFixedSchedule && (
            <Alert severity="warning">{t('passes.swap.immediate_fixed_schedule_warning')}</Alert>
          )}

          {inlineError && (
            <Alert severity="error">
              {inlineError}
              {providerError && (
                <Box component="details" mt={1}>
                  <Box component="summary" sx={{ cursor: 'pointer' }}>
                    <Typography variant="caption">{t('passes.swap.provider_details')}</Typography>
                  </Box>
                  <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', m: 0 }}>
                    {providerError}
                  </Typography>
                </Box>
              )}
            </Alert>
          )}

          {target && targetIsFixed && (
            <>
              <Divider />
              <FixedSchedulePicker
                passOrderPage={target}
                accountId={account.id}
                value={selections}
                onChange={(next) => {
                  setInlineError(null);
                  setSelections(next);
                }}
                refreshKey={refreshKey}
                minSelections={requiredSlots}
                maxSelections={requiredSlots}
              />
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t('common.cancel')}
        </Button>
        <LoadingButton
          variant="contained"
          onClick={handleSubmit}
          loading={submitting}
          disabled={!target || !slotsSatisfied}>
          {t('passes.swap.confirm_cta')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
